import { addChipsToUrlParams } from 'helpers/chipSerialization';
import { SHOW_ALL_OPTION } from 'components/Elements/Filters/constants';
import { ChipTypeEnums } from 'constants/enums';

// Quick Filter constants for analytics navigation
const QUICK_FILTER_VALUES = {
	FUNCTIONAL_LOCATION_ONLY: 'functional_location_only'
};

const URL_PARAMS = {
	QUICK_FILTER: 'quickFilter',
	QUICK_FILTER_CLEAR: 'quickFilterClear'
};

// API parameter mapping for drilldown navigation
// Maps API parameter names to URL parameter names, with URL-specific overrides
const API_PARAMS_TO_URL_PARAM_MAPPING = {
	// Default mappings (apply to all URLs)
	default: {
		siteDepartmentId: 'department',
		// Add other API parameters here as needed
		// defectTypeId: 'defectType',
		// riskRatingId: 'riskRating',
	},
	// URL-specific mappings (override defaults for specific target URLs)
	urlSpecific: {
		'/app/services': {
			status: 'serviceStatus',
			// Add other service-specific mappings here
		},
    '/app/defects': {
      defectStatusId: 'defectStatus',
      defectStatusType: 'defectStatus',
    },
		'/app/models': {
		  status: 'modelStatus',
		},
	}
};

// Whitelist of filter names that should be URL parameters for specific target URLs
// Everything else gets converted to chips if it's a recognized parameter
const URL_PARAM_WHITELIST = {
	'/app/models': ['modelStatus', 'timeframe', 'department'],
	'/app/defects': ['defectStatus', 'timeframe', 'department'],
	'/app/services': ['serviceStatus', 'timeframe', 'department']
};

// Mapping of parameter names to chip types for non-whitelisted parameters
const PARAM_TO_CHIP_TYPE_MAPPING = {
	defectRiskRatingId: ChipTypeEnums.DefectRiskRatingID,
	riskRatingId: ChipTypeEnums.DefectRiskRatingID,
	defectTypeId: ChipTypeEnums.DefectTypeID,
	roleId: ChipTypeEnums.Role,
	defectStatusId: ChipTypeEnums.DefectStatusID,
	siteAssetId: ChipTypeEnums.SiteAssetID,
    modelId: ChipTypeEnums.ModelID,
    defectSystemId: ChipTypeEnums.DefectSystemID,
};

/**
 * Modern URL-based navigation utility that leverages Filter Framework
 * Replaces the old localStorage-based approach with clean URL parameter handling
 * 
 * @param {string} targetUrl - The destination URL
 * @param {Object} filterValues - Current filter values from FilterContext
 * @param {Object} additionalParams - Additional parameters from drilldown or data points (can contain chips array)
 * @param {string} siteAppId - Current site application ID
 */
export const navigateWithFilters = (targetUrl, filterValues, additionalParams = {}, siteAppId = null) => {
	if (!targetUrl) return;

	// Always clear any existing temporary asset chip data first, but check if localStorage is available
	if (localStorage && typeof localStorage.removeItem === 'function') {
		localStorage.removeItem('temp_asset_drilldown');
	}

	// Parse existing parameters from the target URL
	const [baseUrl, existingParams] = targetUrl.split('?');
	const urlParams = new URLSearchParams(existingParams || '');

	// Always preserve siteAppId if available
	if (siteAppId) {
		urlParams.set('siteAppId', siteAppId);
	}

	// Get the whitelisted filter names for this target URL
	const whitelistedParams = getWhitelistedParams(targetUrl);
	const chipsFromFilters = [];

	// Process filter values from reports (department, timeframe, etc.) - skip chips since reports don't use them
	let hasDefectStatusFilter = false;
	Object.entries(filterValues).forEach(([filterType, filterData]) => {
		if (filterType !== 'chips' && filterData?.value) {
			// Special mapping for defectStatusType to defectStatus for URL parameters
			let mappedFilterType = filterType;
			if (filterType === 'defectStatusType' && targetUrl && targetUrl.includes('/app/defects')) {
				mappedFilterType = 'defectStatus';
				hasDefectStatusFilter = true;
			}

			// Track if we have any defectStatus-related filters
			if (filterType === 'defectStatus' && filterData.value?.id !== '' && filterData.value?.id !== null) {
				hasDefectStatusFilter = true;
			}

			// Check if this filter type is whitelisted for URL params
			if (whitelistedParams.includes(mappedFilterType) || whitelistedParams.includes(filterType)) {
				// Special handling for multiselect department filter
				if (filterType === 'department' && Array.isArray(filterData.value)) {
					// Check if "Show All" is selected in the array
					const hasShowAll = filterData.value.some(dept =>
						dept?.id === '' || dept?.id === 'all' || (dept?.id === '' && dept?.name === 'Show All')
					);

					if (hasShowAll) {
						// If "Show All" is selected, add department=all to URL
						urlParams.set('department', 'all');
					} else {
						// For multiselect departments, add each department as a separate URL parameter
						filterData.value.forEach(dept => {
							if (dept?.id && dept.id !== '' && dept.id !== 'all') {
								urlParams.append('department', dept.id);
							}
						});
					}
				} else {
					// Add whitelisted filters as URL parameters using their value.id
					// Special handling for "Show All" option - use 'all' instead of empty string
					if (filterData.value.id !== undefined && filterData.value.id !== null) {
						if (filterData.value.id === "" && filterData.value.name === SHOW_ALL_OPTION.name) {
							urlParams.set(mappedFilterType, 'all');
						} else if (filterData.value.id !== '') {
							urlParams.set(mappedFilterType, filterData.value.id);
						}
					}
				}
			} else {
				// Convert non-whitelisted filters to chips if they have recognized parameter types
				if (filterData.params) {
					Object.entries(filterData.params).forEach(([paramKey, paramValue]) => {
						if (paramValue !== undefined && paramValue !== '' && paramValue !== null && typeof paramValue !== 'object') {
							const chipType = getChipTypeForParam(paramKey);
							if (chipType) {
								chipsFromFilters.push({
									chipType,
									id: paramValue,
									value: filterData.value?.name || "Show All"
								});
							}
						}
					});
				}
			}
		}
	});

	// If this is a defects URL and no defect status filter was found, add defectStatus=all
	if (targetUrl && targetUrl.includes('/app/defects') && !hasDefectStatusFilter && !urlParams.has('defectStatus')) {
		urlParams.set('defectStatus', 'all');
	}

	// Combine chips from filters and drilldown/dataPoint navigation
	const allChips = [...chipsFromFilters];
	if (additionalParams.chips && Array.isArray(additionalParams.chips)) {
		allChips.push(...additionalParams.chips);
	}
	
	// Check for asset chips in drilldown navigation
	const assetChips = allChips.filter(chip => chip.chipType === ChipTypeEnums.SiteAssetID);
	if (assetChips.length > 0 && localStorage && typeof localStorage.setItem === 'function') {
		// Only use the first asset chip to avoid duplicates
		const assetChip = assetChips[0];
		const departmentId = filterValues?.department?.value?.id;
		
		// Store asset chip in localStorage for one-time use with a timestamp
		const tempData = {
			chips: [assetChip], // Only store one asset chip
			department: departmentId,
			timestamp: Date.now(), // Add timestamp for cleanup validation
			isUsed: false // Flag to track if the chip has been used
		};
		localStorage.setItem('temp_asset_drilldown', JSON.stringify(tempData));
		
		// Set up auto-cleanup after navigation with a longer timeout
		// This gives the target page more time to load and use the data
		setTimeout(() => {
			try {
				// Check if localStorage is still available
				if (!localStorage || typeof localStorage.getItem !== 'function') {
					return;
				}
				
				// Check if the chip data exists and if it's been used
				const storedData = localStorage.getItem('temp_asset_drilldown');
				if (storedData) {
					const parsedData = JSON.parse(storedData);
					// Only remove if it's our data (check timestamp) or if it's been used
					if (parsedData.timestamp === tempData.timestamp || parsedData.isUsed) {
						localStorage.removeItem('temp_asset_drilldown');
					}
				}
			} catch (error) {
				// If there's any error, clean up anyway - but only if localStorage is available
				if (localStorage && typeof localStorage.removeItem === 'function') {
					localStorage.removeItem('temp_asset_drilldown');
				}
			}
		}, 2000); // Increased from 500ms to 2000ms
	}
	
	// Add chips to URL if any exist
	if (allChips.length > 0) {
		addChipsToUrlParams(urlParams, allChips);
	}

	// Add any additional non-chip parameters from drilldown or data points
	Object.entries(additionalParams).forEach(([key, value]) => {
		if (key !== 'chips' && value !== undefined && value !== '' && value !== null) {
			urlParams.set(key, value);
		}
	});

	// Build final URL with parameters
	const finalUrl = `${baseUrl}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;

	// Open in new tab
	window.open(finalUrl, '_blank');
};

/**
 * Helper function to get whitelisted URL parameters for a target URL
 * @param {string} targetUrl - The destination URL
 * @returns {Array} Array of parameter names that should be URL parameters
 */
const getWhitelistedParams = (targetUrl) => {
	if (!targetUrl) return [];
	
	const matchingUrlPattern = Object.keys(URL_PARAM_WHITELIST).find(urlPattern => 
		targetUrl.includes(urlPattern)
	);
	
	return matchingUrlPattern ? URL_PARAM_WHITELIST[matchingUrlPattern] : [];
};

/**
 * Helper function to check if a parameter should be converted to a chip
 * @param {string} paramKey - The parameter key
 * @returns {string|null} The chip type if it should be a chip, null otherwise
 */
const getChipTypeForParam = (paramKey) => {
	return PARAM_TO_CHIP_TYPE_MAPPING[paramKey] || null;
};

/**
 * Helper function to get URL-specific parameter mappings
 * @param {string} targetUrl - The destination URL
 * @returns {Object} URL-specific parameter mappings
 */
const getUrlSpecificMappings = (targetUrl) => {
	if (!targetUrl) return {};
	
	const matchingUrlPattern = Object.keys(API_PARAMS_TO_URL_PARAM_MAPPING.urlSpecific).find(urlPattern => 
		targetUrl.includes(urlPattern)
	);
	
	return matchingUrlPattern ? API_PARAMS_TO_URL_PARAM_MAPPING.urlSpecific[matchingUrlPattern] : {};
};

/**
 * Helper function to transform drilldown history and data points into navigation parameters
 * Creates chips and URL parameters that match DefectContent's expected structure
 * 
 * @param {Array} drilldownHistory - History of drilldown actions
 * @param {Object} dataPoint - Current data point being navigated from
 * @param {string} targetUrl - The destination URL (used for URL-specific parameter mapping)
 * @returns {Object} Object containing chips array and additional URL parameters
 */
export const buildNavigationParams = (drilldownHistory = [], dataPoint = null, targetUrl = null) => {
	const chips = [];
	const urlParams = {};

	// Get URL-specific mappings for parameter transformation
	const defaultMappings = API_PARAMS_TO_URL_PARAM_MAPPING.default || {};
	const urlSpecificMappings = targetUrl ? getUrlSpecificMappings(targetUrl) : {};
	const allMappings = { ...defaultMappings, ...urlSpecificMappings };

	// Process drilldown history for both chip creation and parameter mapping
	drilldownHistory.forEach(historyItem => {
		if (historyItem.params) {
			// Add parameters with URL-aware mapping
			Object.entries(historyItem.params).forEach(([paramKey, paramValue]) => {
				if (paramValue !== undefined && paramValue !== '' && paramValue !== null) {
					// Skip object parameters only
					if (typeof paramValue !== 'object') {
						// Apply URL-specific mapping if available, otherwise use original key
						const mappedKey = allMappings[paramKey] || paramKey;
						urlParams[mappedKey] = paramValue;
					}
				}
			});
			
			// Create chips from drilldown parameters with names preserved
			if (historyItem.params.defectRiskRatingId) {
				chips.push({
					chipType: ChipTypeEnums.DefectRiskRatingID,
					id: historyItem.params.defectRiskRatingId,
					value: historyItem.params.riskRatings?.name || "Show All"
				});
			}
			
			if (historyItem.params.defectTypeId) {
				chips.push({
					chipType: ChipTypeEnums.DefectTypeID,
					id: historyItem.params.defectTypeId,
					value: historyItem.params.defectType?.name || "Show All"
				});
			}
			
			if (historyItem.params.roleId) {
				chips.push({
					chipType: ChipTypeEnums.Role,
					id: historyItem.params.roleId,
					value: historyItem.params.role?.name || historyItem.params.roleName || "Show All"
				});
			}
			
			if (historyItem.params.defectSystemId) {
				chips.push({
					chipType: ChipTypeEnums.DefectSystemID,
					id: historyItem.params.defectSystemId,
					value: historyItem.params.defectSystem?.name || "Show All"
				});
			}
		}
	});

	// Add dataPoint-specific chips with names preserved
	if (dataPoint) {
		// For status-level data points (depth === 'S'), add defect status chip
		if (dataPoint.depth === 'S') {
			chips.push({
				chipType: ChipTypeEnums.DefectStatusID,
				id: dataPoint.id,
				value: dataPoint.name || "Show All"
			});
		}

		// For asset-level data points (depth === 'A' from API response)
		if (dataPoint.depth === 'A') {
			// Handle defects with no asset number (id === null)
			// Enable "Functional Location Only" filter for these cases
			if (dataPoint.id === null) {
				// Add quickFilter parameter with 'functional_location_only' selected
				// This will be picked up by the defect list page to enable the quick filter
				urlParams[URL_PARAMS.QUICK_FILTER] = QUICK_FILTER_VALUES.FUNCTIONAL_LOCATION_ONLY;
			}
			// Only create asset chip if id is not null
			else if (dataPoint.id) {
				// When navigating to a specific asset, clear the quickFilter to prevent
				// "Functional Location Only" from persisting from previous navigation
				urlParams[URL_PARAMS.QUICK_FILTER_CLEAR] = 'true';

				// Add asset chip (both risk rating drilldown and regular cases create the same chip)
				chips.push({
					chipType: ChipTypeEnums.SiteAssetID,
					id: dataPoint.id,
					value: dataPoint.name || ""
				});
			}
		}

		// Add defect type chip with name
		if (dataPoint.defectTypeId) {
			chips.push({
				chipType: ChipTypeEnums.DefectTypeID,
				id: dataPoint.defectTypeId,
				value: dataPoint.typeName || dataPoint.name || "Show All"
			});
		}

		// Add risk rating chip with name - only if this isn't an asset in a risk rating drilldown
		if (dataPoint.defectRiskRatingId) {
			const isRiskRatingDrilldown = drilldownHistory.some(item => 
				item.params && item.params.defectRiskRatingId
			);
			
			// Only add a risk rating chip if we're not in a risk rating drilldown with an asset
			const isAssetInRiskRatingDrilldown = isRiskRatingDrilldown && 
				dataPoint.id && drilldownHistory.length > 0;
				
			if (!isAssetInRiskRatingDrilldown) {
				chips.push({
					chipType: ChipTypeEnums.DefectRiskRatingID,
					id: dataPoint.defectRiskRatingId,
					value: dataPoint.riskRatingName || dataPoint.name || "Show All"
				});
			}
		}
		
		// Add role chip with name
		if (dataPoint.roleId) {
			chips.push({
				chipType: ChipTypeEnums.Role,
				id: dataPoint.roleId,
				value: dataPoint.roleName || dataPoint.name || "Show All"
			});
		}
		
		// Add system chip with name
		if (dataPoint.defectSystemId) {
			chips.push({
				chipType: ChipTypeEnums.DefectSystemID,
				id: dataPoint.defectSystemId,
				value: dataPoint.systemName || dataPoint.name || "Show All"
			});
		}
	}

	return { chips, ...urlParams };
};